import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Grid,
} from '@material-ui/core';
import { ArrowBack, ExpandMore } from '@material-ui/icons';
import { HomeVisitRecord, HomeVisitElement } from '../../../constants/Records';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    breadcrumb: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    backButton: {
      marginRight: theme.spacing(2),
    },
    form: {
      padding: theme.spacing(3),
    },
    section: {
      marginBottom: theme.spacing(3),
    },
    sectionTitle: {
      fontWeight: 'bold',
      marginBottom: theme.spacing(2),
    },
    fieldRow: {
      marginBottom: theme.spacing(2),
    },
    accordion: {
      marginBottom: theme.spacing(1),
    },
    accordionSummary: {
      backgroundColor: theme.palette.grey[100],
    },
    categoryTitle: {
      fontWeight: 'bold',
    },
    tableContainer: {
      marginTop: theme.spacing(1),
    },
    headerCell: {
      fontWeight: 'bold',
      textAlign: 'center',
      backgroundColor: theme.palette.grey[200],
      padding: theme.spacing(1),
    },
    itemCell: {
      padding: theme.spacing(1),
      minWidth: 120,
    },
    levelCell: {
      padding: theme.spacing(0.5),
      minWidth: 100,
    },
    problemCell: {
      padding: theme.spacing(0.5),
      textAlign: 'center',
      width: 80,
    },
    envCell: {
      padding: theme.spacing(0.5),
      minWidth: 200,
    },
    envInput: {
      width: '100%',
    },
    detailField: {
      marginTop: theme.spacing(2),
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(2),
      marginTop: theme.spacing(3),
    },
    description: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2),
    },
    timeFields: {
      display: 'flex',
      gap: theme.spacing(2),
      alignItems: 'center',
    },
  }),
);

// 自立度レベル
const INDEPENDENCE_LEVELS = [
  { value: 0, label: '自立' },
  { value: 1, label: '見守り' },
  { value: 2, label: '一部介助' },
  { value: 3, label: '全介助' },
];

// ADL項目
const ADL_ITEMS = [
  { field: 'adl_feed', label: '食事' },
  { field: 'adl_ur', label: '排泄' },
  { field: 'adl_bath', label: '入浴' },
  { field: 'adl_dress', label: '更衣' },
  { field: 'adl_cosme', label: '整容' },
  { field: 'adl_transfer', label: '移乗' },
];

// IADL項目
const IADL_ITEMS = [
  { field: 'iadl_indoor', label: '屋内移動' },
  { field: 'iadl_outdoor', label: '屋外移動' },
  { field: 'iadl_lift', label: '階段昇降' },
  { field: 'iadl_cook', label: '調理' },
  { field: 'iadl_wash', label: '洗濯' },
  { field: 'iadl_clean', label: '掃除' },
];

// 居宅内動作項目
const BEHAVIOR_ITEMS = [
  { field: 'behaivior_arise', label: '起き上がり' },
  { field: 'behaivior_sitting', label: '座位' },
  { field: 'behaivior_standup', label: '立ち上がり' },
  { field: 'behaivior_upright', label: '立位' },
];

interface Props {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  currentRecord: HomeVisitRecord | null;
  isEdit: boolean;
  executing: boolean;
  created: boolean;
  updated: boolean;
  onFetchRecord: (uniqueId: string, sequence: number) => void;
  onCreate: (uniqueId: string, data: Partial<HomeVisitRecord>) => void;
  onUpdate: (uniqueId: string, sequence: number, data: Partial<HomeVisitRecord>) => void;
  onResetFlags: () => void;
}

// 初期要素
const createInitialElement = (): HomeVisitElement => ({
  level: [0],
  is_problem: false,
  env: '',
});

// 日付をYYYY-MM-DD形式に正規化
const normalizeDate = (dateStr: string | undefined): string => {
  if (!dateStr) {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }
  // "yyyy-MM-dd HH:mm-HH:mm" 形式から日付部分を抽出
  const datePart = dateStr.split(' ')[0];
  const cleaned = datePart.replace(/\//g, '-');
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
    return cleaned;
  }
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// 時刻を抽出
const extractTimes = (visitedDay: string | undefined): { start: string; end: string } => {
  if (!visitedDay) {
    return { start: '09:00', end: '10:00' };
  }
  // "yyyy-MM-dd HH:mm-HH:mm" 形式
  const parts = visitedDay.split(' ');
  if (parts.length >= 2) {
    const times = parts[1].split('-');
    return {
      start: times[0] || '09:00',
      end: times[1] || '10:00',
    };
  }
  return { start: '09:00', end: '10:00' };
};

const HomeVisitRecordForm: React.FC<Props> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  currentRecord,
  isEdit,
  executing,
  created,
  updated,
  onCreate,
  onUpdate,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  // 訪問情報
  const [visitDate, setVisitDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [visitTimeStart, setVisitTimeStart] = useState('09:00');
  const [visitTimeEnd, setVisitTimeEnd] = useState('10:00');
  const [staff, setStaff] = useState('');
  const [profession, setProfession] = useState('');
  const [interviewee, setInterviewee] = useState('');

  // ADL/IADL/居宅内動作
  const [adlElements, setAdlElements] = useState<Record<string, HomeVisitElement>>(() => {
    const initial: Record<string, HomeVisitElement> = {};
    ADL_ITEMS.forEach(item => {
      initial[item.field] = createInitialElement();
    });
    return initial;
  });
  const [adlDetail, setAdlDetail] = useState('');

  const [iadlElements, setIadlElements] = useState<Record<string, HomeVisitElement>>(() => {
    const initial: Record<string, HomeVisitElement> = {};
    IADL_ITEMS.forEach(item => {
      initial[item.field] = createInitialElement();
    });
    return initial;
  });
  const [iadlDetail, setIadlDetail] = useState('');

  const [behaviorElements, setBehaviorElements] = useState<Record<string, HomeVisitElement>>(() => {
    const initial: Record<string, HomeVisitElement> = {};
    BEHAVIOR_ITEMS.forEach(item => {
      initial[item.field] = createInitialElement();
    });
    return initial;
  });
  const [behaviorDetail, setBehaviorDetail] = useState('');

  // 展開状態
  const [expandedSections, setExpandedSections] = useState<string[]>(['訪問情報', 'ADL', 'IADL', '居宅内動作']);

  useEffect(() => {
    if (currentRecord && isEdit) {
      // 訪問情報を復元
      setVisitDate(normalizeDate(currentRecord.visited_day));
      const times = extractTimes(currentRecord.visited_day);
      setVisitTimeStart(times.start);
      setVisitTimeEnd(times.end);
      setStaff(currentRecord.staff || '');
      setProfession(currentRecord.profession || '');
      setInterviewee(currentRecord.interviewee || '');

      // ADL要素を復元
      const newAdl: Record<string, HomeVisitElement> = {};
      ADL_ITEMS.forEach(item => {
        const element = (currentRecord as any)[item.field] as HomeVisitElement | undefined;
        newAdl[item.field] = element || createInitialElement();
      });
      setAdlElements(newAdl);
      setAdlDetail(currentRecord.adl_detail || '');

      // IADL要素を復元
      const newIadl: Record<string, HomeVisitElement> = {};
      IADL_ITEMS.forEach(item => {
        const element = (currentRecord as any)[item.field] as HomeVisitElement | undefined;
        newIadl[item.field] = element || createInitialElement();
      });
      setIadlElements(newIadl);
      setIadlDetail(currentRecord.iadl_detail || '');

      // 居宅内動作要素を復元
      const newBehavior: Record<string, HomeVisitElement> = {};
      BEHAVIOR_ITEMS.forEach(item => {
        const element = (currentRecord as any)[item.field] as HomeVisitElement | undefined;
        newBehavior[item.field] = element || createInitialElement();
      });
      setBehaviorElements(newBehavior);
      setBehaviorDetail(currentRecord.behaivior_detail || '');
    }
  }, [currentRecord, isEdit]);

  useEffect(() => {
    if (created || updated) {
      onResetFlags();
      history.push(`/institutions/${institutionId}/users/${userId}/records/homevisit`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, updated]);

  const handleBack = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/homevisit`);
  };

  const handleAccordionChange = (section: string) => (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpandedSections(prev => (isExpanded ? [...prev, section] : prev.filter(s => s !== section)));
  };

  const handleElementChange = (
    category: 'adl' | 'iadl' | 'behavior',
    field: string,
    property: 'level' | 'is_problem' | 'env',
    value: any,
  ) => {
    const setter = category === 'adl' ? setAdlElements : category === 'iadl' ? setIadlElements : setBehaviorElements;

    setter(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [property]: value,
      },
    }));
  };

  const handleSubmit = () => {
    // visited_day を "yyyy-MM-dd HH:mm-HH:mm" 形式で構築
    const visitedDay = `${visitDate} ${visitTimeStart}-${visitTimeEnd}`;

    const data: Partial<HomeVisitRecord> = {
      staff: staff || undefined,
      profession: profession || undefined,
      visited_day: visitedDay,
      interviewee: interviewee || undefined,
      // ADL
      ...adlElements,
      adl_detail: adlDetail || undefined,
      // IADL
      ...iadlElements,
      iadl_detail: iadlDetail || undefined,
      // 居宅内動作
      ...behaviorElements,
      behaivior_detail: behaviorDetail || undefined,
    };

    if (isEdit && currentRecord) {
      onUpdate(uniqueId, currentRecord.sequence, data);
    } else {
      onCreate(uniqueId, data);
    }
  };

  const renderEvaluationTable = (
    items: { field: string; label: string }[],
    elements: Record<string, HomeVisitElement>,
    category: 'adl' | 'iadl' | 'behavior',
  ) => (
    <TableContainer className={classes.tableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>項目</TableCell>
            <TableCell className={classes.headerCell}>自立度</TableCell>
            <TableCell className={classes.headerCell}>問題</TableCell>
            <TableCell className={classes.headerCell}>環境・備考</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <TableRow key={item.field}>
              <TableCell className={classes.itemCell}>{item.label}</TableCell>
              <TableCell className={classes.levelCell}>
                <FormControl fullWidth size="small">
                  <Select
                    value={elements[item.field]?.level?.[0] ?? 0}
                    onChange={e => handleElementChange(category, item.field, 'level', [e.target.value as number])}
                  >
                    {INDEPENDENCE_LEVELS.map(level => (
                      <MenuItem key={level.value} value={level.value}>
                        {level.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell className={classes.problemCell}>
                <Checkbox
                  checked={elements[item.field]?.is_problem || false}
                  onChange={e => handleElementChange(category, item.field, 'is_problem', e.target.checked)}
                  color="primary"
                />
              </TableCell>
              <TableCell className={classes.envCell}>
                <TextField
                  value={elements[item.field]?.env || ''}
                  onChange={e => handleElementChange(category, item.field, 'env', e.target.value)}
                  size="small"
                  className={classes.envInput}
                  placeholder="環境・備考"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div className={classes.root}>
      <Typography variant="body2" className={classes.breadcrumb}>
        {institutionName} &gt; {userName} &gt; 訪問記録 &gt; {isEdit ? '編集' : '新規作成'}
      </Typography>

      <div className={classes.header}>
        <IconButton className={classes.backButton} onClick={handleBack}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5">{isEdit ? '訪問記録編集' : '訪問記録作成'}</Typography>
      </div>

      <Paper className={classes.form}>
        <Typography variant="body2" className={classes.description}>
          各項目の自立度と問題の有無を評価してください。
        </Typography>

        {/* 訪問情報 */}
        <Accordion
          expanded={expandedSections.includes('訪問情報')}
          onChange={handleAccordionChange('訪問情報')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
            <Typography className={classes.categoryTitle}>訪問情報</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="訪問日"
                  type="date"
                  value={visitDate}
                  onChange={e => setVisitDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  label="開始時刻"
                  type="time"
                  value={visitTimeStart}
                  onChange={e => setVisitTimeStart(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  label="終了時刻"
                  type="time"
                  value={visitTimeEnd}
                  onChange={e => setVisitTimeEnd(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="スタッフ名" value={staff} onChange={e => setStaff(e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="職種" value={profession} onChange={e => setProfession(e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="聴取対象者"
                  value={interviewee}
                  onChange={e => setInterviewee(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ADL */}
        <Accordion
          expanded={expandedSections.includes('ADL')}
          onChange={handleAccordionChange('ADL')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
            <Typography className={classes.categoryTitle}>ADL（日常生活動作）</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ flexDirection: 'column' }}>
            {renderEvaluationTable(ADL_ITEMS, adlElements, 'adl')}
            <TextField
              label="ADL詳細・備考"
              value={adlDetail}
              onChange={e => setAdlDetail(e.target.value)}
              fullWidth
              multiline
              rows={2}
              className={classes.detailField}
            />
          </AccordionDetails>
        </Accordion>

        {/* IADL */}
        <Accordion
          expanded={expandedSections.includes('IADL')}
          onChange={handleAccordionChange('IADL')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
            <Typography className={classes.categoryTitle}>IADL（手段的日常生活動作）</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ flexDirection: 'column' }}>
            {renderEvaluationTable(IADL_ITEMS, iadlElements, 'iadl')}
            <TextField
              label="IADL詳細・備考"
              value={iadlDetail}
              onChange={e => setIadlDetail(e.target.value)}
              fullWidth
              multiline
              rows={2}
              className={classes.detailField}
            />
          </AccordionDetails>
        </Accordion>

        {/* 居宅内動作 */}
        <Accordion
          expanded={expandedSections.includes('居宅内動作')}
          onChange={handleAccordionChange('居宅内動作')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
            <Typography className={classes.categoryTitle}>居宅内動作</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ flexDirection: 'column' }}>
            {renderEvaluationTable(BEHAVIOR_ITEMS, behaviorElements, 'behavior')}
            <TextField
              label="居宅内動作詳細・備考"
              value={behaviorDetail}
              onChange={e => setBehaviorDetail(e.target.value)}
              fullWidth
              multiline
              rows={2}
              className={classes.detailField}
            />
          </AccordionDetails>
        </Accordion>

        <div className={classes.buttonContainer}>
          <Button variant="outlined" onClick={handleBack} disabled={executing}>
            キャンセル
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={executing || !visitDate}>
            {executing ? <CircularProgress size={24} /> : isEdit ? '更新' : '作成'}
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export default HomeVisitRecordForm;
